/**
 * /api/system/* — server health + per-card session resource metrics.
 *
 * Phase 12 extends the original /system/load with:
 *   - /system/sessions/:id/load  — live cAdvisor read for a card session
 *   - /system/sessions/estimate  — pre-launch RAM/CPU estimate + risk
 *
 * Polling cadences (TanStack Query staleTime + refetchInterval):
 *   - load        10s
 *   - session     5s   (Resources tab — real-time feel)
 *   - estimate   N/A   (one-shot per card open)
 *
 * REMOVED 2026-07-26: `activeSessions()` → GET /system/sessions/active, and the
 * <ActiveSessions> widget that was its only caller. The gateway route is gone
 * (apps/chaos-gateway/src/index.ts, attachGuarded — it could never satisfy
 * activeSessionsResponseSchema, had no org fence, and its consumer was never
 * mounted). The zod schema in @chaos/types/system is intentionally kept as the
 * spec for a future, org-scoped replacement.
 */
import type {
  AiUsage,
  SessionEstimate,
  SessionLoad,
  SystemLoad,
} from '@chaos/types/system';

import { apiJson } from './client';

export type { AiUsage, SessionEstimate, SessionLoad, SystemLoad };

/**
 * ── WHOSE SUBSCRIPTION IS THIS? (SPEC-03 / FD-A, 2026-08-01) ────────────
 *
 * `PER_ACCOUNT_CLI_CREDENTIALS_ENABLED` is ON in production, so a card spends
 * the RUNNER's Claude/Codex subscription, not "the instance's". Until now this
 * endpoint described ONE tree and no pixel said which — so a second person's
 * exhausted subscription was visible nowhere while the scheduler's capacity gate
 * silently held their cards.
 *
 * The response is now a superset: every field `AiUsage` already had, unchanged
 * and in place (so the panel's existing numbers cannot move), plus `accounts`.
 *
 * `accounts` CARRIES NO PERCENTAGES, on purpose. What an operator needs is
 * "whose cards am I holding, and until when". How much of his own subscription
 * another person has spent is his business — the same rule that keeps one
 * person's credentials invisible to everyone else.
 *
 * The account the primary tree resolves to is NOT in this array (the gateway
 * de-duplicates it by realpath), so on a single-person box the array is empty
 * and nothing new renders at all.
 *
 * Typed here rather than in `@chaos/types/system` because that package is a
 * shared surface owned elsewhere; `apiJson` performs no runtime parse, so this
 * is the type the callers actually get.
 */
export interface VendorWindowHold {
  /** True when the capacity gate would refuse this account's next run. */
  blocked: boolean;
  /** ISO reset of the window that is holding; null when nothing is holding. */
  resetsAt: string | null;
  stale: boolean;
}

export interface AccountUsageStatus {
  accountId: string;
  claude: VendorWindowHold | null;
  codex: VendorWindowHold | null;
  fetchedAt: string | null;
}

export interface AiUsageOverview extends AiUsage {
  /**
   * WHOSE the top-level numbers are. The gateway has sent this since
   * 2026-08-01 and no pixel read it, which is why an unqualified percentage
   * kept reading as "the instance's". `accountId` is null when the primary
   * tree cannot be mapped to an account row — honest about not knowing.
   */
  accountScope: { accountId: string | null; isPrimary: boolean };
  accounts: AccountUsageStatus[];
  /**
   * VEN-1 (sweep 2026-09-18). `false` means the gateway's usage telemetry is
   * administratively off (SUBSCRIPTION_USAGE_TELEMETRY_ENABLED, default and
   * current-production value) — `claude`/`codex` will stay `null` forever,
   * not just until the next poll. Distinguishes that permanent state from a
   * transient "no reading yet" so the panel can say which one it is instead
   * of showing the same unfilled bar for both.
   *
   * VEN-1 review (FR, 2026-09-18): this is ONE flag shared by both vendors,
   * but only `codex` has a writer at all in this wave — see `claudeSource`
   * below. Prefer the per-vendor source over this field: a panel keyed only
   * to `telemetryEnabled` reads `claude` as merely "pending" the moment the
   * founder turns telemetry on, which is the original VEN-1 symptom back.
   */
  telemetryEnabled: boolean;
  /**
   * VEN-1 review (FR, 2026-09-18). Per-vendor provenance for why `claude`/
   * `codex` is `null` — see the matching (canonical) doc comment on the
   * gateway's `AiUsageOverview` (`apps/chaos-gateway/src/lib/ai-usage-poller.ts`).
   *   - "off"         — telemetry is administratively off; not polled at all.
   *   - "unsupported" — telemetry is on, but no sanctioned probe/writer
   *                      exists for this vendor (today: always `claude`).
   *                      Permanent regardless of the flag.
   *   - "pending"      — telemetry is on, a writer exists, no reading yet.
   *                      Transient.
   *   - "live"         — telemetry is on and at least one reading landed.
   */
  claudeSource: UsageSource;
  codexSource: UsageSource;
}

export type UsageSource = 'off' | 'unsupported' | 'pending' | 'live';

export const systemApi = {
  load(): Promise<SystemLoad> {
    return apiJson<SystemLoad>('/system/load');
  },
  aiUsage(): Promise<AiUsageOverview> {
    return apiJson<AiUsageOverview>('/system/ai-usage');
  },
  sessionLoad(sessionId: string): Promise<SessionLoad> {
    return apiJson<SessionLoad>(`/system/sessions/${sessionId}/load`);
  },
  estimate(model: string): Promise<SessionEstimate> {
    return apiJson<SessionEstimate>(`/system/sessions/estimate?model=${encodeURIComponent(model)}`);
  },
};

export const systemKeys = {
  all: ['system'] as const,
  load: () => [...systemKeys.all, 'load'] as const,
  aiUsage: () => [...systemKeys.all, 'ai-usage'] as const,
  sessionLoad: (sessionId: string) => [...systemKeys.all, 'session', sessionId, 'load'] as const,
  estimate: (model: string) => [...systemKeys.all, 'sessions', 'estimate', model] as const,
};
